<template>
  <div v-if="textVersion == 'aboutTheAct'">
    <h2>When did it enter into force?</h2>
    <p>
      On 1 July 2022 the Norwegian Transparency Act entered into force. The
      first report must be published by 30th June 2023.
    </p>

    <h2>What does the Act require?</h2>
    <p>
      The Act requires enterprises to map their entire supply chain as far as
      possible and proportional, and perform a risk assessment. For business
      partners, the due diligence is restricted to the business partner’s own
      operations.
    </p>
    <p>
      PwC’s Transparency Chain is a digital tool to help enterprises in this
      process of mapping and risk assessing.
    </p>
    <p>
      Our customer has considered your company as a supplier if Transparency
      Chain asks you to
    </p>
    <ol>
      <li>Fill out the questionnaire</li>
      <li>Upload your suppliers</li>
      <li>Forward the questionnaire to your supplier</li>
    </ol>
    <p>
      Our customer has considered your company as a business partner if
      Transparency Chain only asks you to fill out the questionnaire.
    </p>

    <h2>
      Why are companies not subject to the Act asked to fill out a
      questionnaire?
    </h2>
    <p>
      If your company is not subject to the Act, you may still be asked by
      companies to fill out a questionnaire in Transparency Chain. Smaller
      enterprises are indirectly affected because large enterprises will require
      information from smaller enterprises in the due diligence process. The
      obligation to perform due diligence is required regardless of which
      countries the supply chain or business partner is based.
    </p>
  </div>

  <div v-if="textVersion == 'aboutTransparencyChain'">
    <p>
      This section contains information on how to use Transparency Chain
      depending on whether you are a customer using Transparency Chain to map
      business relationships or if you are a supplier or business partner who
      has received a questionnaire.
    </p>
    <v-divider class="my-6" />
    <v-tabs v-model="tab">
      <v-tab value="customer"> Customers </v-tab>
      <v-tab value="supplier"> Suppliers </v-tab>
      <v-tab value="bp"> Business partners </v-tab>
    </v-tabs>
    <v-row>
      <v-col>
        <v-window v-model="tab">
          <v-window-item value="customer">
            <v-row>
              <v-col>
                <p>
                  Transparency Chain is a digital tool used to
                  <strong>map</strong> and <strong>trace</strong> your supply
                  chain, and how they are working to protect human rights and
                  decent working conditions.
                </p>
                <p>To get started:</p>
                <ol>
                  <li>Register your suppliers and business partners</li>
                  <li>Choose a questionnaire and set a deadline</li>
                  <li>
                    Send the questionnaire to your selected business
                    relationships
                  </li>
                </ol>
                <p>
                  Transparency Chain automatically sends out reminders to
                  suppliers and business partners who have not completed their
                  tasks on time.
                </p>
                <h3>Add colleagues as users</h3>
                <p>
                  You can also add colleagues as users in the “Users” section.
                </p>

                <h3>Dashboard</h3>
                <p>
                  Once your business relationships start to fill out the
                  questionnaire, the dashboard will display the results. The
                  dashboard widgets regarding labour and human rights, health
                  and safety, business integrity, environment and supplier
                  management displays companies whose questionnaire response has
                  been flagged as a risk.
                </p>

                <p>
                  By clicking a widget, you get to a page with an overview of
                  all flagged companies in each category. Here you can also
                  click on a company and see their unit profile.
                </p>

                <p>
                  The unit profile contains information about the company and
                  provides an overview of the flagged responses across labour
                  and human rights, health and safety, business integrity,
                  environment and supplier management. You can also view the
                  company’s questionnaire responses in its entirety.
                </p>
                <h3>Questionnaires</h3>
                <p>
                  This page is where you can view the status of all companies
                  who have received the questionnaire.
                </p>
                <h3>Definition of suppliers and business partners</h3>
                <p>
                  The act defines <em>supply chain</em> as any party in the
                  chain of suppliers and sub-suppliers that supplies or produces
                  goods, services or other input factors included in an
                  enterprise's delivery of services or production of goods from
                  the raw material stage to a finished product. This definition
                  is meant to correspond with the definition of supply chain as
                  the EU Conflict Minerals Regulation.
                </p>
                <ul>
                  <li>
                    The supply chain encompasses all parties involved in the
                    process of moving and processing a product from a raw
                    material to a finished product.
                  </li>
                  <li>
                    Input factors include, but are not limited to, raw
                    materials, components, services and transportation services.
                  </li>
                </ul>
                <p>
                  The act defines <em>business partners</em> as any party
                  providing goods or services, but that are not part of the
                  supply chain. Together with <em>supply chain</em>, this
                  definition is meant to correspond with the definition of
                  business relations in the OECD guidelines for multinational
                  companies.
                </p>
                <ul>
                  <li>
                    Typical business partners include the cleaning company that
                    cleans your office, your marketing agency and the company
                    providing you with office chairs.
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-window-item>
          <v-window-item value="supplier">
            <v-row>
              <v-col>
                <p>To get started:</p>
                <ol>
                  <li>Register your company</li>
                  <li>Fill out the questionnaire</li>
                  <li>
                    Upload a list of suppliers relevant to your delivery of
                    products/services to the company who sent you the
                    questionnaire
                  </li>
                  <li>Send out the questionnaire</li>
                </ol>
                <p>
                  You can also add colleagues as users in the “Users” section in
                  order to collaborate on the tasks.
                </p>
                <h3>Which suppliers should I send the questionnaire to?</h3>
                <p>
                  You are asked to only upload those suppliers relevant to your
                  delivery of products/services to the company who sent you the
                  questionnaire.
                </p>
                <p>
                  We have included information below about who is considered as
                  a “supplier” according to the Transparency Act. If you are
                  still not sure which suppliers to upload, please get in touch
                  with either the company who sent you the questionnaire or the
                  PwC team at transparencychain@pwc.com.
                </p>

                <p>
                  The act defines supply chain as any party in the chain of
                  suppliers and sub-suppliers that supplies or produces goods,
                  services or other input factors included in an enterprise's
                  delivery of services or production of goods from the raw
                  material stage to a finished product. This definition is meant
                  to correspond with the definition of supply chain as the EU
                  Conflict Minerals Regulation.
                </p>
                <ul>
                  <li>
                    The supply chain encompasses all parties involved in the
                    process of moving and processing a product from a raw
                    material to a finished product.
                  </li>
                  <li>
                    Input factors include, but are not limited to, raw
                    materials, components, services and transportation services.
                  </li>
                </ul>
              </v-col>
            </v-row>
          </v-window-item>
          <v-window-item value="bp">
            <v-row>
              <v-col>
                <p>To get started:</p>
                <ol>
                  <li>Register your company</li>
                  <li>Fill out the questionnaire</li>
                </ol>
                <p>
                  You can also add colleagues as users in the “Users” section in
                  order to collaborate on the tasks.
                </p>
              </v-col>
            </v-row>
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
defineProps<{
  textVersion: "aboutTheAct" | "aboutTransparencyChain";
}>();

const tab = ref(null);
</script>

<style scoped lang="scss">
p {
  margin-top: 8px;
}
h2 {
  margin-top: 16px;
}
h3 {
  margin-top: 16px;
}
ol {
  margin-left: 40px;
}
ul {
  margin-left: 40px;
}
</style>
